import React from 'react'
import { createPortal } from 'react-dom'
import { Icon } from './icons'
import {
  bareId,
  blockIconUrl,
  derivedBlockIconUrl,
  enchantmentLabel,
  itemIconUrl,
  overrideIcon,
  parseEnchantment,
  prettyId,
  renderChestIconUrl,
} from './mcAssets'
import type {
  InventoryEditEnchantment,
  InventoryEditItem,
  InventoryEditRequest,
  InventoryItem,
  InventorySlotChange,
  ItemRegistry,
  ItemRegistryEnchantment,
  ItemRegistryItem,
  PlayerSnapshot,
  ShulkerContents,
} from './types'

interface InventoryEditorModalProps {
  snapshot: PlayerSnapshot
  registry: ItemRegistry | null
  registryLoading: boolean
  registryError: string | null
  onClose: () => void
  onSave: (request: InventoryEditRequest) => Promise<boolean>
  onFetchShulker?: (slot: number) => Promise<ShulkerContents | null>
  onSaveShulker?: (slot: number, request: InventoryEditRequest) => Promise<boolean>
}

interface ShulkerEditState {
  outerSlot: number
  outerId: string
  contents: InventoryItem[]   // 27 entries; empty inner slots are null InventoryItems? — sparse list, indexed by slot
  drafts: DraftMap
  selected: number
  loading: boolean
  error: string | null
}

interface DraftItem {
  id: string
  count: number
  damage: number | null
  customName: string
  enchantments: InventoryEditEnchantment[]
}

type DraftValue = DraftItem | null
type DraftMap = Record<number, DraftValue>

const MAIN_ROWS: number[][] = [
  [9, 10, 11, 12, 13, 14, 15, 16, 17],
  [18, 19, 20, 21, 22, 23, 24, 25, 26],
  [27, 28, 29, 30, 31, 32, 33, 34, 35],
]
const HOTBAR = [0, 1, 2, 3, 4, 5, 6, 7, 8]
const ARMOR = [103, 102, 101, 100]
const SHULKER_ROWS: number[][] = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8],
  [9, 10, 11, 12, 13, 14, 15, 16, 17],
  [18, 19, 20, 21, 22, 23, 24, 25, 26],
]
const SHULKER_SIZE = 27

function isShulker(id: string | undefined | null): boolean {
  if (!id) return false
  const bare = id.includes(':') ? id.split(':')[1] : id
  return bare.endsWith('shulker_box')
}

export function InventoryEditorModal({
  snapshot,
  registry,
  registryLoading,
  registryError,
  onClose,
  onSave,
  onFetchShulker,
  onSaveShulker,
}: InventoryEditorModalProps) {
  const [selectedSlot, setSelectedSlot] = React.useState(0)
  const [drafts, setDrafts] = React.useState<DraftMap>({})
  const [unsafeEnchantments, setUnsafeEnchantments] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [shulker, setShulker] = React.useState<ShulkerEditState | null>(null)

  React.useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (shulker) {
        // Inside the shulker drill-in, Escape returns to the player inventory
        // rather than closing the whole editor.
        closeShulker()
      } else {
        onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, shulker])

  const playerOriginalBySlot = React.useMemo(() => {
    const map = new Map<number, InventoryItem>()
    for (const item of snapshot.inventory) map.set(item.slot, item)
    for (const item of snapshot.armor) map.set(item.slot, item)
    if (snapshot.offhand) map.set(-106, snapshot.offhand)
    return map
  }, [snapshot])

  const shulkerOriginalBySlot = React.useMemo(() => {
    if (!shulker) return new Map<number, InventoryItem>()
    const map = new Map<number, InventoryItem>()
    for (const item of shulker.contents) map.set(item.slot, item)
    return map
  }, [shulker])

  // Active editing context — switches between player inventory and the
  // currently-open shulker.
  const inShulker = shulker !== null
  const originalBySlot = inShulker ? shulkerOriginalBySlot : playerOriginalBySlot
  const activeDrafts = inShulker ? shulker!.drafts : drafts
  const activeSelected = inShulker ? shulker!.selected : selectedSlot

  const registryMaps = React.useMemo(() => buildRegistryMaps(registry), [registry])
  const current = draftForSlot(activeSelected, activeDrafts, originalBySlot)
  const original = originalBySlot.get(activeSelected)
  const locked = original?.editable === false
  const selectedIsShulker = !inShulker && (current?.id ? isShulker(current.id) : false)
  const dirtySlots = React.useMemo(
    () => Object.keys(activeDrafts).map(Number).sort((a, b) => a - b),
    [activeDrafts],
  )
  const effectiveDirtySlots = React.useMemo(
    () => dirtySlots.filter(slot => {
      const draft = activeDrafts[slot]
      if (draft === null) return true
      return Boolean(draft.id.trim())
    }),
    [dirtySlots, activeDrafts],
  )
  const validationErrors = React.useMemo(
    () => validateDrafts(activeDrafts, originalBySlot, registryMaps, unsafeEnchantments),
    [activeDrafts, originalBySlot, registryMaps, unsafeEnchantments],
  )
  const canSave = effectiveDirtySlots.length > 0 && validationErrors.length === 0 && !registryLoading && !saving
    && (!inShulker || !shulker?.loading)

  // ----- mutators (active-context-aware) -----
  const setActiveDrafts = (updater: (prev: DraftMap) => DraftMap) => {
    if (inShulker) {
      setShulker(prev => prev ? { ...prev, drafts: updater(prev.drafts) } : prev)
    } else {
      setDrafts(updater)
    }
  }
  const setActiveSelected = (next: number) => {
    if (inShulker) {
      setShulker(prev => prev ? { ...prev, selected: next } : prev)
    } else {
      setSelectedSlot(next)
    }
  }

  const selectSlot = (next: number) => {
    if (next === activeSelected) return
    setActiveDrafts(prev => {
      const draft = prev[activeSelected]
      if (draft === undefined || draft === null) return prev
      if (!draft.id.trim()) {
        const updated = { ...prev }
        delete updated[activeSelected]
        return updated
      }
      const filtered = draft.enchantments.filter(e => e.id.trim())
      if (filtered.length === draft.enchantments.length) return prev
      return { ...prev, [activeSelected]: { ...draft, enchantments: filtered } }
    })
    setActiveSelected(next)
  }

  const updateSelected = (updater: (item: DraftItem) => DraftItem) => {
    if (locked) return
    const base = current ?? createEmptyItem()
    setActiveDrafts(prev => ({ ...prev, [activeSelected]: updater(base) }))
  }

  const clearSelected = () => {
    if (locked) return
    setActiveDrafts(prev => ({ ...prev, [activeSelected]: null }))
  }

  const resetSelected = () => {
    setActiveDrafts(prev => {
      const next = { ...prev }
      delete next[activeSelected]
      return next
    })
  }

  const resetPriorWork = () => {
    if (locked || !original) return
    setActiveDrafts(prev => ({ ...prev, [activeSelected]: fromInventoryItem(original) }))
  }

  // ----- shulker drill-in -----
  function openShulker(outerSlot: number) {
    if (!onFetchShulker) return
    const orig = playerOriginalBySlot.get(outerSlot)
    if (!orig || !isShulker(orig.id)) return
    setShulker({
      outerSlot,
      outerId: orig.id,
      contents: [],
      drafts: {},
      selected: 0,
      loading: true,
      error: null,
    })
    void onFetchShulker(outerSlot)
      .then(res => {
        if (!res) {
          setShulker(prev => prev && prev.outerSlot === outerSlot
            ? { ...prev, loading: false, error: 'Could not load shulker contents' }
            : prev)
          return
        }
        setShulker(prev => prev && prev.outerSlot === outerSlot
          ? { ...prev, contents: res.items, loading: false }
          : prev)
      })
      .catch(err => {
        const msg = err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: string }).message ?? 'Fetch failed')
          : 'Fetch failed'
        setShulker(prev => prev && prev.outerSlot === outerSlot
          ? { ...prev, loading: false, error: msg }
          : prev)
      })
  }

  function closeShulker() {
    if (!shulker) return
    const hasInnerEdits = Object.values(shulker.drafts).some(d => d !== undefined)
    if (hasInnerEdits) {
      const ok = window.confirm('Discard unsaved shulker edits?')
      if (!ok) return
    }
    setShulker(null)
  }

  const save = async () => {
    if (!canSave) return
    setSaving(true)
    try {
      const draftsForSave = activeDrafts
      const changes = dirtySlots.flatMap<InventorySlotChange>(slot => {
        const draft = draftsForSave[slot]
        if (draft === null) return [{ slot, item: null }]
        if (!draft.id.trim()) return []
        const cleaned: DraftItem = {
          ...draft,
          enchantments: draft.enchantments.filter(e => e.id.trim()),
        }
        return [{ slot, item: toWireItem(cleaned, registryMaps) }]
      })
      const request: InventoryEditRequest = { unsafeEnchantments, changes }
      if (inShulker && onSaveShulker && shulker) {
        const ok = await onSaveShulker(shulker.outerSlot, request)
        if (ok) {
          // Stay in the modal but exit shulker mode so the user sees a fresh
          // outer view and can re-drill if they want.
          setShulker(null)
        }
      } else {
        const ok = await onSave(request)
        if (ok) onClose()
      }
    } finally {
      setSaving(false)
    }
  }

  return createPortal(
    <div className="modal-overlay" onMouseDown={event => { if (event.target === event.currentTarget) onClose() }}>
      <div className="modal inventory-editor-modal">
        <div className="inventory-editor-head">
          <div>
            <h2>{inShulker ? 'Edit Shulker' : 'Edit Inventory'}</h2>
            <div className="dim text-xs mono">
              {inShulker
                ? `${snapshot.name} · ${slotLabel(shulker!.outerSlot)} · ${prettyId(shulker!.outerId)}`
                : snapshot.name}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {inShulker && (
              <button className="btn sm ghost" onClick={closeShulker} title="Back to inventory">
                <Icon name="chevron-left" size={13} /> Back
              </button>
            )}
            <button className="btn sm ghost icon" onClick={onClose} title="Close">
              <Icon name="x" size={13} />
            </button>
          </div>
        </div>

        {registryError && (
          <div className="banner err" style={{ marginBottom: 12 }}>
            <Icon name="alert-triangle" size={14} />
            <span>{registryError}</span>
          </div>
        )}

        {inShulker && shulker?.error && (
          <div className="banner err" style={{ marginBottom: 12 }}>
            <Icon name="alert-triangle" size={14} />
            <span>{shulker.error}</span>
          </div>
        )}

        <div className="inventory-editor-layout">
          <div className="inventory-editor-grid-wrap">
            {inShulker ? (
              shulker!.loading ? (
                <div className="dim text-sm" style={{ padding: 24 }}>Loading shulker contents…</div>
              ) : (
                <div className="mc-inv-layout inventory-editor-grid">
                  <div className="mc-inv-col">
                    {SHULKER_ROWS.map((row, i) => (
                      <div key={i} className="mc-inv-grid">
                        {row.map(slot => (
                          <EditableSlot
                            key={slot}
                            slot={slot}
                            item={draftForSlot(slot, shulker!.drafts, shulkerOriginalBySlot)}
                            original={shulkerOriginalBySlot.get(slot)}
                            selected={shulker!.selected === slot}
                            onSelect={selectSlot}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )
            ) : (
              <div className="mc-inv-layout inventory-editor-grid">
                <div className="mc-armor-col">
                  {ARMOR.map(slot => (
                    <EditableSlot
                      key={slot}
                      slot={slot}
                      item={draftForSlot(slot, drafts, playerOriginalBySlot)}
                      original={playerOriginalBySlot.get(slot)}
                      selected={selectedSlot === slot}
                      placeholder={ARMOR_PLACEHOLDER[slot]}
                      onSelect={selectSlot}
                    />
                  ))}
                </div>
                <div className="mc-inv-col">
                  <div className="mc-inv-grid">
                    {MAIN_ROWS.flat().map(slot => (
                      <EditableSlot
                        key={slot}
                        slot={slot}
                        item={draftForSlot(slot, drafts, playerOriginalBySlot)}
                        original={playerOriginalBySlot.get(slot)}
                        selected={selectedSlot === slot}
                        onSelect={selectSlot}
                      />
                    ))}
                  </div>
                  <div className="mc-inv-grid mc-hotbar">
                    {HOTBAR.map(slot => (
                      <EditableSlot
                        key={slot}
                        slot={slot}
                        item={draftForSlot(slot, drafts, playerOriginalBySlot)}
                        original={playerOriginalBySlot.get(slot)}
                        selected={selectedSlot === slot}
                        onSelect={selectSlot}
                      />
                    ))}
                  </div>
                </div>
                <div className="mc-offhand-col">
                  <EditableSlot
                    slot={-106}
                    item={draftForSlot(-106, drafts, playerOriginalBySlot)}
                    original={playerOriginalBySlot.get(-106)}
                    selected={selectedSlot === -106}
                    placeholder="OH"
                    onSelect={selectSlot}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="inventory-editor-panel">
            <div className="inventory-editor-slot-title">
              <span>{inShulker ? `Shulker · ${innerSlotLabel(activeSelected)}` : slotLabel(activeSelected)}</span>
              {locked && <span className="chip"><Icon name="shield" size={11} /> Locked</span>}
            </div>

            {!locked && original && (original.repairCost ?? 0) > 0 && (
              <div className="inventory-editor-prior-work">
                <span className="dim text-xs">
                  Prior anvil work cost: {original.repairCost} level{original.repairCost === 1 ? '' : 's'}
                </span>
                {activeSelected in activeDrafts ? (
                  <span className="dim text-xs">Will reset on save</span>
                ) : (
                  <button className="btn sm ghost" onClick={resetPriorWork}>
                    <Icon name="rotate-ccw" size={11} /> Reset on save
                  </button>
                )}
              </div>
            )}

            {selectedIsShulker && onFetchShulker && (
              <div className="inventory-editor-prior-work">
                <span className="dim text-xs">This slot holds a shulker box.</span>
                <button
                  className="btn sm primary"
                  disabled={effectiveDirtySlots.length > 0}
                  title={effectiveDirtySlots.length > 0 ? 'Save or reset other edits before opening' : 'Open shulker contents'}
                  onClick={() => openShulker(activeSelected)}
                >
                  <Icon name="package" size={11} /> Open shulker
                </button>
              </div>
            )}

            {registryLoading ? (
              <div className="dim text-sm">Loading registry...</div>
            ) : locked ? (
              <ReadOnlyItem item={original ?? null} />
            ) : current ? (
              <ItemForm
                item={current}
                registry={registry}
                itemInfo={registryMaps.items.get(normalizeId(current.id))}
                enchantments={registryMaps.enchantments}
                unsafeEnchantments={unsafeEnchantments}
                onChange={updateSelected}
              />
            ) : (
              <button
                className="btn primary"
                disabled={!registry || registry.items.length === 0}
                onClick={() => updateSelected(item => item)}
              >
                <Icon name="plus" size={13} /> Add item
              </button>
            )}

            <label className="check inventory-editor-safe-toggle">
              <input
                type="checkbox"
                checked={unsafeEnchantments}
                onChange={event => setUnsafeEnchantments(event.target.checked)}
              />
              <span>Unsafe enchantments</span>
            </label>

            {validationErrors.length > 0 && (
              <div className="banner warn inventory-editor-errors">
                <Icon name="alert-triangle" size={14} />
                <div>{validationErrors[0]}</div>
              </div>
            )}

            <div className="inventory-editor-panel-actions">
              <button className="btn ghost" disabled={!current || locked} onClick={clearSelected}>
                <Icon name="trash" size={13} /> Clear
              </button>
              <button className="btn ghost" disabled={!(activeSelected in activeDrafts)} onClick={resetSelected}>
                <Icon name="rotate-ccw" size={13} /> Reset
              </button>
              <button className="btn primary ml-auto" disabled={!canSave} onClick={() => { void save() }}>
                <Icon name="check" size={13} /> {saving ? 'Saving...' : (inShulker ? 'Save shulker' : 'Save')}
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>,
    document.body,
  )
}

function ItemForm({
  item,
  registry,
  itemInfo,
  enchantments,
  unsafeEnchantments,
  onChange,
}: {
  item: DraftItem
  registry: ItemRegistry | null
  itemInfo?: ItemRegistryItem
  enchantments: Map<string, ItemRegistryEnchantment>
  unsafeEnchantments: boolean
  onChange: (updater: (item: DraftItem) => DraftItem) => void
}) {
  const maxCount = itemInfo?.maxStackSize ?? 64
  const damageable = itemInfo?.damageable === true
  const maxDamage = itemInfo?.maxDamage ?? 0

  const itemId = normalizeId(item.id)
  const applicableEnchants = React.useMemo(() => {
    if (!registry) return []
    if (unsafeEnchantments) return registry.enchantments
    const hasApplicabilityData = registry.enchantments.some(e => (e.applicableItems?.length ?? 0) > 0)
    if (!hasApplicabilityData) return registry.enchantments
    return registry.enchantments.filter(e => e.applicableItems?.includes(itemId))
  }, [registry, unsafeEnchantments, itemId])

  return (
    <div className="inventory-editor-form">
      <div className="field-row">
        <label>Item</label>
        <Combobox
          value={item.id}
          options={registry?.items ?? []}
          labelOf={prettyId}
          placeholder="Search item"
          emptyLabel="No items match"
          onCommit={next => onChange(prev => ({ ...prev, id: next }))}
        />
      </div>
      <div className="inventory-editor-two">
        <div className="field-row">
          <label>Count</label>
          <IntField
            min={1}
            max={maxCount}
            value={item.count}
            onCommit={next => onChange(prev => ({ ...prev, count: next }))}
          />
        </div>
        <div className="field-row">
          <label>Damage</label>
          <IntField
            min={0}
            max={maxDamage}
            disabled={!damageable}
            value={damageable ? (item.damage ?? 0) : 0}
            onCommit={next => onChange(prev => ({ ...prev, damage: next }))}
          />
        </div>
      </div>
      <div className="field-row">
        <label>Custom name</label>
        <input
          value={item.customName}
          maxLength={128}
          onChange={event => onChange(prev => ({ ...prev, customName: event.target.value }))}
          placeholder={prettyId(item.id)}
        />
      </div>
      <div className="inventory-editor-enchants">
        <div className="inventory-editor-subhead">
          <span>Enchantments</span>
          <button
            className="btn sm ghost"
            disabled={applicableEnchants.length === 0}
            onClick={() => onChange(prev => ({
              ...prev,
              enchantments: [...prev.enchantments, { id: '', level: 1 }],
            }))}
          >
            <Icon name="plus" size={11} /> Add
          </button>
        </div>
        {item.enchantments.length === 0 ? (
          <div className="dim text-xs">None</div>
        ) : (
          item.enchantments.map((enchant, index) => {
            const info = enchantments.get(normalizeId(enchant.id))
            const min = unsafeEnchantments ? 1 : info?.startLevel ?? 1
            const max = unsafeEnchantments ? 255 : info?.maxLevel ?? 255
            return (
              <div key={index} className="inventory-editor-enchant-row">
                <Combobox
                  value={enchant.id}
                  options={applicableEnchants}
                  labelOf={enchantmentLabel}
                  placeholder="Search enchantment"
                  emptyLabel="No applicable enchantments"
                  onCommit={next => onChange(prev => replaceEnchant(prev, index, { ...enchant, id: next }))}
                />
                <IntField
                  min={min}
                  max={max}
                  value={enchant.level}
                  onCommit={next => onChange(prev => replaceEnchant(prev, index, { ...enchant, level: next }))}
                />
                <button
                  className="btn sm ghost icon"
                  title="Remove enchantment"
                  onClick={() => onChange(prev => ({
                    ...prev,
                    enchantments: prev.enchantments.filter((_, i) => i !== index),
                  }))}
                >
                  <Icon name="x" size={11} />
                </button>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

function ReadOnlyItem({ item }: { item: InventoryItem | null }) {
  if (!item) return <div className="dim text-sm">Empty slot</div>
  return (
    <div className="inventory-editor-readonly">
      <InventoryIcon id={item.id} />
      <div>
        <div>{item.customName || prettyId(item.id)}</div>
        <div className="dim text-xs mono">{item.id}</div>
      </div>
    </div>
  )
}

function EditableSlot({
  slot,
  item,
  original,
  selected,
  placeholder,
  onSelect,
}: {
  slot: number
  item: DraftItem | null
  original?: InventoryItem
  selected: boolean
  placeholder?: string
  onSelect: (slot: number) => void
}) {
  const locked = original?.editable === false
  const hasItem = item !== null && item.id.trim().length > 0
  return (
    <button
      type="button"
      className={`mc-inv-slot inventory-editor-slot${selected ? ' selected' : ''}${locked ? ' locked' : ''}`}
      aria-label={hasItem ? `${slotLabel(slot)}, ${prettyId(item!.id)}` : `${slotLabel(slot)}, empty`}
      onClick={() => onSelect(slot)}
    >
      {hasItem ? (
        <>
          <InventoryIcon id={item!.id} />
          {item!.count > 1 && <span className="mc-slot-count mono">{item!.count}</span>}
          {item!.damage != null && item!.damage > 0 && <DurabilityBar damage={item!.damage} />}
        </>
      ) : (
        placeholder && <span className="mc-slot-placeholder">{placeholder}</span>
      )}
      {locked && <span className="inventory-editor-lock"><Icon name="shield" size={10} /></span>}
    </button>
  )
}

function buildRegistryMaps(registry: ItemRegistry | null) {
  const enchants = registry?.enchantments ?? []
  return {
    items: new Map((registry?.items ?? []).map(item => [normalizeId(item.id), item])),
    enchantments: new Map(enchants.map(enchant => [normalizeId(enchant.id), enchant])),
    applicableItems: new Map(enchants.map(enchant => [
      normalizeId(enchant.id),
      new Set((enchant.applicableItems ?? []).map(normalizeId)),
    ])),
  }
}

function draftForSlot(slot: number, drafts: DraftMap, originalBySlot: Map<number, InventoryItem>): DraftItem | null {
  if (Object.prototype.hasOwnProperty.call(drafts, slot)) return drafts[slot] ? { ...(drafts[slot] as DraftItem) } : null
  const original = originalBySlot.get(slot)
  return original ? fromInventoryItem(original) : null
}

function fromInventoryItem(item: InventoryItem): DraftItem {
  return {
    id: normalizeId(item.id),
    count: item.count,
    damage: item.damage ?? null,
    customName: item.customName ?? '',
    enchantments: item.enchantments.map(raw => {
      const parsed = parseEnchantment(raw)
      return { id: normalizeId(parsed.id), level: parsed.level }
    }),
  }
}

function createEmptyItem(): DraftItem {
  return { id: '', count: 1, damage: null, customName: '', enchantments: [] }
}

function validateDrafts(
  drafts: DraftMap,
  originalBySlot: Map<number, InventoryItem>,
  registryMaps: ReturnType<typeof buildRegistryMaps>,
  unsafe: boolean,
): string[] {
  const errors: string[] = []
  for (const [slotText, draft] of Object.entries(drafts)) {
    const slot = Number(slotText)
    if (originalBySlot.get(slot)?.editable === false) {
      errors.push(`${slotLabel(slot)} is locked.`)
      continue
    }
    if (draft === null) continue
    if (!draft.id.trim()) continue
    const itemId = normalizeId(draft.id)
    const itemInfo = registryMaps.items.get(itemId)
    if (!itemInfo) {
      errors.push(`${slotLabel(slot)} uses an unknown item id.`)
      continue
    }
    if (draft.count < 1 || draft.count > itemInfo.maxStackSize) {
      errors.push(`${slotLabel(slot)} count must be 1-${itemInfo.maxStackSize}.`)
    }
    if (draft.damage != null) {
      if (!itemInfo.damageable) errors.push(`${slotLabel(slot)} item cannot take damage.`)
      else if (draft.damage < 0 || draft.damage > itemInfo.maxDamage) {
        errors.push(`${slotLabel(slot)} damage must be 0-${itemInfo.maxDamage}.`)
      }
    }
    const seen = new Set<string>()
    for (const enchant of draft.enchantments) {
      if (!enchant.id.trim()) continue
      const id = normalizeId(enchant.id)
      const info = registryMaps.enchantments.get(id)
      if (!info) {
        errors.push(`${slotLabel(slot)} has an unknown enchantment.`)
        continue
      }
      if (seen.has(id)) errors.push(`${slotLabel(slot)} repeats ${enchantmentLabel(id)}.`)
      seen.add(id)
      if (unsafe) {
        if (enchant.level < 1 || enchant.level > 255) errors.push(`${enchantmentLabel(id)} must be 1-255.`)
      } else if (enchant.level < info.startLevel || enchant.level > info.maxLevel) {
        errors.push(`${enchantmentLabel(id)} must be ${info.startLevel}-${info.maxLevel}.`)
      }
      if (!unsafe) {
        const applicable = registryMaps.applicableItems.get(id)
        if (applicable && applicable.size > 0 && !applicable.has(itemId)) {
          errors.push(`${enchantmentLabel(id)} cannot be applied to ${prettyId(itemId)}.`)
        }
      }
    }
    if (!unsafe) {
      for (let i = 0; i < draft.enchantments.length; i += 1) {
        if (!draft.enchantments[i].id.trim()) continue
        const left = normalizeId(draft.enchantments[i].id)
        const leftInfo = registryMaps.enchantments.get(left)
        if (!leftInfo) continue
        for (let j = i + 1; j < draft.enchantments.length; j += 1) {
          if (!draft.enchantments[j].id.trim()) continue
          const right = normalizeId(draft.enchantments[j].id)
          if (leftInfo.conflicts.map(normalizeId).includes(right)) {
            errors.push(`${enchantmentLabel(left)} conflicts with ${enchantmentLabel(right)}.`)
          }
        }
      }
    }
  }
  return errors
}

function toWireItem(item: DraftItem, registryMaps: ReturnType<typeof buildRegistryMaps>): InventoryEditItem {
  const id = normalizeId(item.id)
  const itemInfo = registryMaps.items.get(id)
  return {
    id,
    count: item.count,
    damage: itemInfo?.damageable ? item.damage ?? 0 : undefined,
    customName: item.customName.trim() ? item.customName.trim() : null,
    enchantments: item.enchantments.map(enchant => ({
      id: normalizeId(enchant.id),
      level: enchant.level,
    })),
  }
}

function replaceEnchant(item: DraftItem, index: number, enchant: InventoryEditEnchantment): DraftItem {
  return {
    ...item,
    enchantments: item.enchantments.map((current, i) => (i === index ? enchant : current)),
  }
}

function normalizeId(id: string): string {
  const trimmed = id.trim()
  return trimmed.includes(':') ? trimmed : `minecraft:${trimmed}`
}

function IntField({
  value,
  min,
  max,
  disabled,
  onCommit,
}: {
  value: number
  min: number
  max: number
  disabled?: boolean
  onCommit: (value: number) => void
}) {
  const [text, setText] = React.useState<string>(String(value))
  React.useEffect(() => { setText(String(value)) }, [value])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value
    setText(raw)
    const parsed = Number.parseInt(raw, 10)
    if (Number.isFinite(parsed) && parsed >= min && parsed <= max) {
      onCommit(parsed)
    }
  }

  const handleBlur = () => {
    const parsed = Number.parseInt(text, 10)
    let final: number
    if (!Number.isFinite(parsed) || parsed < min) final = min
    else if (parsed > max) final = max
    else final = parsed
    setText(String(final))
    if (final !== value) onCommit(final)
  }

  const step = (delta: number) => {
    const parsed = Number.parseInt(text, 10)
    const base = Number.isFinite(parsed) ? parsed : value
    const next = Math.max(min, Math.min(max, base + delta))
    setText(String(next))
    if (next !== value) onCommit(next)
  }

  return (
    <div className={`int-field${disabled ? ' disabled' : ''}`}>
      <input
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        disabled={disabled}
        value={text}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      <div className="int-field-steppers" aria-hidden="true">
        <button
          type="button"
          className="int-field-step"
          tabIndex={-1}
          disabled={disabled || value >= max}
          onMouseDown={event => event.preventDefault()}
          onClick={() => step(1)}
          aria-label="Increase"
        >
          <Icon name="chevron-up" size={10} />
        </button>
        <button
          type="button"
          className="int-field-step"
          tabIndex={-1}
          disabled={disabled || value <= min}
          onMouseDown={event => event.preventDefault()}
          onClick={() => step(-1)}
          aria-label="Decrease"
        >
          <Icon name="chevron-down" size={10} />
        </button>
      </div>
    </div>
  )
}

const COMBOBOX_RENDER_CAP = 150

function Combobox<T extends { id: string }>({
  value,
  options,
  labelOf,
  placeholder,
  emptyLabel,
  onCommit,
}: {
  value: string
  options: T[]
  labelOf: (id: string) => string
  placeholder?: string
  emptyLabel?: string
  onCommit: (id: string) => void
}) {
  const [text, setText] = React.useState<string>(() => labelOf(value))
  const [open, setOpen] = React.useState(false)
  const [highlighted, setHighlighted] = React.useState(0)
  const [menuRect, setMenuRect] = React.useState<{ top: number; left: number; width: number; dropUp: boolean } | null>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const menuRef = React.useRef<HTMLDivElement>(null)
  const optionRefs = React.useRef<Array<HTMLDivElement | null>>([])
  const skipNextBlur = React.useRef(false)

  React.useEffect(() => {
    setText(labelOf(value))
  }, [value, labelOf])

  const filtered = React.useMemo(() => {
    const q = text.trim().toLowerCase()
    const matches = q
      ? options.filter(opt => {
          const label = labelOf(opt.id).toLowerCase()
          const id = bareId(opt.id).toLowerCase()
          return label.includes(q) || id.includes(q)
        })
      : options
    return matches
  }, [text, options, labelOf])

  const visible = filtered.slice(0, COMBOBOX_RENDER_CAP)
  const overflow = filtered.length - visible.length

  const positionMenu = React.useCallback(() => {
    const input = inputRef.current
    if (!input) return
    const rect = input.getBoundingClientRect()
    const menuMax = 280
    const spaceBelow = window.innerHeight - rect.bottom
    const dropUp = spaceBelow < menuMax + 12 && rect.top > spaceBelow
    setMenuRect({
      top: dropUp ? rect.top - 4 : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      dropUp,
    })
  }, [])

  React.useEffect(() => {
    if (!open) return
    positionMenu()
    const handlePointer = (event: MouseEvent) => {
      const target = event.target as Node
      if (inputRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      setOpen(false)
    }
    const handleScrollOrResize = () => positionMenu()
    window.addEventListener('mousedown', handlePointer)
    window.addEventListener('resize', handleScrollOrResize)
    window.addEventListener('scroll', handleScrollOrResize, true)
    return () => {
      window.removeEventListener('mousedown', handlePointer)
      window.removeEventListener('resize', handleScrollOrResize)
      window.removeEventListener('scroll', handleScrollOrResize, true)
    }
  }, [open, positionMenu])

  React.useEffect(() => {
    if (!open) return
    const node = optionRefs.current[highlighted]
    if (node) node.scrollIntoView({ block: 'nearest' })
  }, [highlighted, open])

  const resolveAndCommit = (raw: string) => {
    const trimmed = raw.trim()
    if (!trimmed) {
      setText(labelOf(value))
      return
    }
    const lower = trimmed.toLowerCase()
    const matched = options.find(opt =>
      labelOf(opt.id).toLowerCase() === lower
      || bareId(opt.id).toLowerCase() === lower
      || normalizeId(opt.id).toLowerCase() === lower,
    )
    if (matched) {
      if (matched.id !== value) onCommit(matched.id)
      setText(labelOf(matched.id))
    } else {
      if (trimmed !== value) onCommit(trimmed)
    }
  }

  const commitOption = (option: T) => {
    skipNextBlur.current = true
    onCommit(option.id)
    setText(labelOf(option.id))
    setOpen(false)
    inputRef.current?.focus()
  }

  return (
    <div className="combobox">
      <input
        ref={inputRef}
        value={text}
        spellCheck={false}
        autoComplete="off"
        placeholder={placeholder}
        onChange={event => {
          setText(event.target.value)
          setOpen(true)
          setHighlighted(0)
        }}
        onFocus={() => { setOpen(true); setHighlighted(0) }}
        onBlur={() => {
          if (skipNextBlur.current) {
            skipNextBlur.current = false
            return
          }
          resolveAndCommit(text)
          setOpen(false)
        }}
        onKeyDown={event => {
          if (event.key === 'ArrowDown') {
            event.preventDefault()
            setOpen(true)
            setHighlighted(idx => Math.min(idx + 1, Math.max(visible.length - 1, 0)))
          } else if (event.key === 'ArrowUp') {
            event.preventDefault()
            setHighlighted(idx => Math.max(idx - 1, 0))
          } else if (event.key === 'Enter') {
            if (open && visible[highlighted]) {
              event.preventDefault()
              commitOption(visible[highlighted])
            }
          } else if (event.key === 'Escape') {
            event.preventDefault()
            setOpen(false)
          }
        }}
      />
      {open && menuRect && createPortal(
        <div
          ref={menuRef}
          className={`dropdown-menu sm combobox-menu${menuRect.dropUp ? ' drop-up' : ''}`}
          role="listbox"
          style={{
            top: menuRect.dropUp ? undefined : menuRect.top,
            bottom: menuRect.dropUp ? window.innerHeight - menuRect.top : undefined,
            left: menuRect.left,
            minWidth: menuRect.width,
          }}
        >
          {visible.length === 0 ? (
            <div className="combobox-empty">{emptyLabel ?? 'No matches'}</div>
          ) : (
            <>
              {visible.map((opt, idx) => (
                <div
                  key={opt.id}
                  ref={node => { optionRefs.current[idx] = node }}
                  className={[
                    'dropdown-option',
                    idx === highlighted ? 'highlighted' : '',
                    opt.id === value ? 'selected' : '',
                  ].filter(Boolean).join(' ')}
                  role="option"
                  aria-selected={opt.id === value}
                  onMouseEnter={() => setHighlighted(idx)}
                  onMouseDown={event => event.preventDefault()}
                  onClick={() => commitOption(opt)}
                >
                  <span className="dropdown-option-label">{labelOf(opt.id)}</span>
                </div>
              ))}
              {overflow > 0 && (
                <div className="combobox-overflow">+{overflow} more — keep typing to filter</div>
              )}
            </>
          )}
        </div>,
        document.body,
      )}
    </div>
  )
}

function slotLabel(slot: number): string {
  if (slot === -106) return 'Offhand'
  if (slot === 100) return 'Boots'
  if (slot === 101) return 'Leggings'
  if (slot === 102) return 'Chestplate'
  if (slot === 103) return 'Helmet'
  if (slot >= 0 && slot <= 8) return `Hotbar ${slot + 1}`
  return `Slot ${slot}`
}

function innerSlotLabel(slot: number): string {
  // Shulkers are a 3-row x 9-column grid; render as "row N, col M" so the
  // user can map back to what they see in the editor.
  const row = Math.floor(slot / 9) + 1
  const col = (slot % 9) + 1
  return `Row ${row}, Col ${col}`
}

const ARMOR_PLACEHOLDER: Record<number, string> = {
  100: 'B',
  101: 'L',
  102: 'C',
  103: 'H',
}

type IconStage = 'renderchest' | 'override' | 'item' | 'block' | 'derived' | 'fail'

function InventoryIcon({ id }: { id: string }) {
  const override = React.useMemo(() => overrideIcon(id), [id])
  const [stage, setStage] = React.useState<IconStage>('renderchest')

  React.useEffect(() => setStage('renderchest'), [id])

  if (stage === 'fail') return <span className="mc-slot-fallback mono">{bareId(id).slice(0, 3).toUpperCase()}</span>
  let src: string | null
  if (stage === 'renderchest') src = renderChestIconUrl(id)
  else if (stage === 'override') src = override?.url ?? null
  else if (stage === 'item') src = itemIconUrl(id)
  else if (stage === 'block') src = blockIconUrl(id)
  else src = derivedBlockIconUrl(id)
  if (src == null) return <span className="mc-slot-fallback mono">{bareId(id).slice(0, 3).toUpperCase()}</span>

  const nextStage = (cur: Exclude<IconStage, 'fail'>): IconStage => {
    if (cur === 'renderchest') return override ? 'override' : 'item'
    if (cur === 'override') return 'item'
    if (cur === 'item') return 'block'
    if (cur === 'block') return derivedBlockIconUrl(id) ? 'derived' : 'fail'
    return 'fail'
  }
  const onError = () => setStage(prev => prev === 'fail' ? 'fail' : nextStage(prev))
  if (stage === 'override' && override?.crop && override?.atlas) {
    const [cx, cy, cw, ch] = override.crop
    const [aw, ah] = override.atlas
    return (
      <svg className="mc-slot-img" viewBox={`${cx} ${cy} ${cw} ${ch}`} preserveAspectRatio="xMidYMid meet" overflow="hidden">
        <image href={src} width={aw} height={ah} preserveAspectRatio="none" onError={onError} />
      </svg>
    )
  }
  return <img className="mc-slot-img" src={src} alt="" loading="lazy" referrerPolicy="no-referrer" onError={onError} />
}

function DurabilityBar({ damage }: { damage: number }) {
  const pct = Math.max(0, Math.min(1, damage / 1500))
  const color = pct > 0.5 ? '#f43f5e' : pct > 0.25 ? '#f59e0b' : '#10b981'
  return (
    <div className="mc-slot-dur">
      <div className="mc-slot-dur-fill" style={{ width: `${(1 - pct) * 100}%`, background: color }} />
    </div>
  )
}
